import React, { useEffect, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { getRequest, pathname } from "@react/helpers/fetch"
import Modal  from "@react/shared/ui/Modal"
import Button from "@react/shared/ui/Button"
import Input, { inputVariants } from "@react/shared/ui/Input"
import ClientSitePicker from "@react/quotes/project_quotes_index/components/ClientSitePicker"

// Quick opportunity creation — same shell/pickers as "Nouveau devis chantier"
// (NewQuoteModal), plus Responsable, Portefeuille and Pipeline. The book of
// business follows the responsable and is where the deal is filed; the client
// is searched freely, over what the user is allowed to see.
const NewOpportunityModal = ({
  isOpen, onClose, createUrl, csrfToken,
  pipelines = [], defaultPipelineId,
  owners = [], defaultOwnerId,
  commercialPortfoliosUrl
}) => {
  const firstPipeline = defaultPipelineId || (pipelines[0] && pipelines[0].id) || ""
  const firstOwner    = defaultOwnerId    || (owners[0]    && owners[0].id)    || ""

  const [title,       setTitle]       = useState("")
  const [client,      setClient]      = useState(null)
  const [site,        setSite]        = useState(null)
  const [pipelineId,  setPipelineId]  = useState(firstPipeline)
  const [ownerId,     setOwnerId]     = useState(firstOwner)
  const [portfolioId, setPortfolioId] = useState("")
  const [portfolios,  setPortfolios]  = useState([])

  useEffect(() => {
    if (!isOpen) return
    setTitle(""); setClient(null); setSite(null)
    setPipelineId(firstPipeline)
    setPortfolioId("")
    selectOwner(firstOwner)
  }, [isOpen])

  const byPortfolio = portfolios.length > 0

  const selectOwner = id => {
    setOwnerId(id)
    setPortfolioId(""); setPortfolios([])
    if (!id) return

    getRequest(pathname({ path: commercialPortfoliosUrl, format: "json", params: { commercial_id: id } }))
      .then(data => setPortfolios(Array.isArray(data) ? data : []))
  }

  // The site is what carries the deal to a quote, so it is required here too.
  const canSubmit = title.trim() && pipelineId && ownerId && site?.id && (!byPortfolio || portfolioId)

  const handleConfirm = () => {
    if (!canSubmit) return
    const form = document.createElement("form")
    form.method = "post"
    form.action = createUrl
    const add = (name, value) => {
      if (value == null || value === "") return
      const i = document.createElement("input")
      i.type = "hidden"; i.name = name; i.value = value
      form.appendChild(i)
    }
    add("authenticity_token", csrfToken)
    add("opportunity[title]",       title.trim())
    add("opportunity[owner_id]",    ownerId)
    add("opportunity[client_id]",   client?.id)
    add("opportunity[site_id]",     site?.id)
    add("opportunity[pipeline_id]", pipelineId)
    add("opportunity[portfolio_id]", portfolioId)
    document.body.appendChild(form)
    form.submit()
  }

  // Match the Objet / Client / Site fields exactly (same Input styling).
  const selectCls = `${inputVariants()} pr-8`

  const field = (label, control) => (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-slate-700">{label}</label>
      {control}
    </div>
  )

  return (
    <Modal
      isOpen  = {isOpen}
      onClose = {onClose}
      size    = "lg"
      title   = {I18n.t("crm.opportunities.new.heading")}
      footer  = {
        <>
          <Button variant="ghost" onClick={onClose}>{I18n.t("button.cancel")}</Button>
          <Button variant="primary" onClick={handleConfirm} disabled={!canSubmit}>
            <FontAwesomeIcon icon="plus" />
            {I18n.t("button.create")}
          </Button>
        </>
      }
    >
      {field(I18n.t("crm.opportunities.new.subject"),
        <Input value={title} onChange={e => setTitle(e.target.value)} />)}

      {field(I18n.t("crm.opportunities.new.owner"),
        <select className={selectCls} value={ownerId} onChange={e => selectOwner(e.target.value)}>
          {owners.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>)}

      {byPortfolio && field(I18n.t("crm.opportunities.new.portfolio"),
        <select className={selectCls} value={portfolioId} onChange={e => setPortfolioId(e.target.value)}>
          <option value="">{I18n.t("crm.opportunities.new.portfolio_prompt")}</option>
          {portfolios.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>)}

      <ClientSitePicker
        client         = {client}
        site           = {site}
        onClientChange = {setClient}
        onSiteChange   = {setSite}
        siteRequired
      />

      {field(I18n.t("crm.opportunities.new.pipeline"),
        <select className={selectCls} value={pipelineId} onChange={e => setPipelineId(e.target.value)}>
          {pipelines.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>)}
    </Modal>
  )
}

export default NewOpportunityModal
